import { ColumnDef } from '@tanstack/react-table';
import { Eye, FileText } from 'lucide-react';
import { SetStateAction } from 'react';
import { Pagination } from '../../../../lib/interfaces/utils';
import { cn } from '../../../../lib/utils/cn';
import { formatDateTimeDE } from '../../../../lib/utils/date';
import { ArchiveFilter } from '../../constants/archive-filter.enum';
import { formatFileSize } from '../../lib/file-size';
import { ContractAttachmentListItemResponseDto } from '../../types/response';
import ArchiveBadge from '../archive-badge/archive-badge';
import ArchiveRowAction from '../archive-row-action/archive-row-action';
import DataTable from '../data-table/data-table';
import ListEmptyState from '../list-empty-state/list-empty-state';
import RowActionButton from '../row-action-button/row-action-button';

const EMPTY_STATE: Record<ArchiveFilter, { title: string; description: string }> = {
  [ArchiveFilter.ACTIVE]: {
    title: 'Keine aktiven Mail-Anhänge',
    description: 'Lade z. B. die AGB als PDF hoch, damit sie mit der Vertragsbestätigung verschickt werden.'
  },
  [ArchiveFilter.ARCHIVED]: { title: 'Keine archivierten Mail-Anhänge', description: 'Archivierte Mail-Anhänge erscheinen hier.' },
  [ArchiveFilter.ALL]: { title: 'Noch keine Mail-Anhänge', description: 'Lade deinen ersten Mail-Anhang hoch, um loszulegen.' }
};

export interface AttachmentListProps {
  attachments: ContractAttachmentListItemResponseDto[];
  filter: ArchiveFilter;
  pagination: Pagination;
  pageCount: number;
  onPaginationChange: (next: SetStateAction<Pagination>) => void;
  isLoading: boolean;
  onEdit: (attachment: ContractAttachmentListItemResponseDto) => void;
  onViewFile: (attachment: ContractAttachmentListItemResponseDto) => void;
  onArchiveToggle: (attachment: ContractAttachmentListItemResponseDto) => void;
}

const AttachmentList = ({
  attachments,
  filter,
  pagination,
  pageCount,
  onPaginationChange,
  isLoading,
  onEdit,
  onViewFile,
  onArchiveToggle
}: AttachmentListProps) => {
  const columns: ColumnDef<ContractAttachmentListItemResponseDto>[] = [
    {
      id: 'title',
      header: 'Mail-Anhang',
      cell: ({ row }) => (
        <span className={cn('block truncate text-sm font-bold text-[#12282A]', row.original.isArchived && 'text-[#888]')} title={row.original.title}>
          {row.original.title}
        </span>
      )
    },
    {
      id: 'fileName',
      header: 'Dateiname',
      size: 260,
      cell: ({ row }) => (
        <span className="flex items-center gap-2 min-w-0 text-sm text-gray-700">
          <FileText className="h-4 w-4 shrink-0 text-[#888]" />
          <span className="truncate" title={row.original.fileName}>
            {row.original.fileName}
          </span>
        </span>
      )
    },
    {
      id: 'size',
      header: 'Größe',
      size: 100,
      cell: ({ row }) => <span className="text-sm text-gray-700 whitespace-nowrap">{formatFileSize(row.original.file.size)}</span>
    },
    {
      id: 'status',
      header: 'Status',
      size: 140,
      cell: ({ row }) => <ArchiveBadge isArchived={row.original.isArchived} />
    },
    {
      id: 'createdAt',
      header: 'Erstellt',
      size: 180,
      cell: ({ row }) => <span className="text-sm text-gray-700 whitespace-nowrap">{formatDateTimeDE(row.original.createdAt)}</span>
    },
    {
      id: 'actions',
      header: '',
      size: 104,
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          <RowActionButton label="PDF ansehen" onClick={() => onViewFile(row.original)}>
            <Eye className="h-4 w-4" />
          </RowActionButton>
          <ArchiveRowAction isArchived={row.original.isArchived} onClick={() => onArchiveToggle(row.original)} />
        </div>
      )
    }
  ];

  return (
    <DataTable
      columns={columns}
      data={attachments}
      pagination={pagination}
      pageCount={pageCount}
      onPaginationChange={onPaginationChange}
      isLoading={isLoading}
      onRowClick={onEdit}
      emptyState={<ListEmptyState {...EMPTY_STATE[filter]} />}
    />
  );
};

export default AttachmentList;
